import React from "react"
import classNames from "classnames"

import Icon from "@UILibrary/Icon"
import { Role } from "@components/RoleSelection"

import styles from "./sidenav.module.sass"

/**
 * The always-expanded left sidebar. This is a purely presentational component:
 * all routing/permission/role data is resolved by the Navigation container and
 * passed in. Desktop-only by design (Wealthbit does not support mobile).
 */

type SideNavItem = {
  id?: number
  label: string
  icon?: string
  href: string
  isActive?: boolean
}

type SideNavContext = {
  title?: string
  backLabel?: string
  backHref?: string
  items: SideNavItem[]
}

type SideNavTenant = {
  name?: string
  logo?: { url?: string }
}

type SideNavUser = {
  fullName?: string
  organization?: string
  role?: string
}

type SideNavProps = {
  tenant: SideNavTenant
  currentUser: SideNavUser
  items: SideNavItem[]
  context?: SideNavContext
  helpPath?: string
  settingsPath?: string
  roles: Role[]
  onRoleSelected: (role: Role) => void
  onLogout: () => void
}

const SideNav = ({
  tenant,
  currentUser,
  items,
  context,
  helpPath,
  settingsPath,
  roles,
  onRoleSelected,
  onLogout,
}: SideNavProps) => {
  const [switcherOpen, setSwitcherOpen] = React.useState(false)
  // The rail rests collapsed (icon-only) and expands while the mouse is over it.
  const [collapsed, setCollapsed] = React.useState(true)
  const collapseTimer = React.useRef<number>()
  const isHovering = React.useRef(false)
  // Tracked separately from hover: focus can leave while the pointer is still
  // parked over the rail (and vice-versa). Collapse only when neither holds.
  const isFocused = React.useRef(false)
  const footerRef = React.useRef<HTMLDivElement>(null)
  const profileButtonRef = React.useRef<HTMLButtonElement>(null)
  const popupRef = React.useRef<HTMLDivElement>(null)

  const closeSwitcher = React.useCallback(() => {
    setSwitcherOpen(false)
    profileButtonRef.current?.focus()
  }, [])

  // Delay before collapsing on mouse leave so small, imprecise mouse movements
  // out of the rail don't cause it to snap shut.
  const COLLAPSE_DELAY_MS = 250

  const expand = React.useCallback(() => {
    isHovering.current = true
    window.clearTimeout(collapseTimer.current)
    setCollapsed(false)
  }, [])

  const handleFocus = React.useCallback(() => {
    isFocused.current = true
    window.clearTimeout(collapseTimer.current)
    setCollapsed(false)
  }, [])

  // Schedule a delayed collapse. The timer only collapses if, when it fires, the
  // mouse has actually left the rail and the profile switcher popup is closed —
  // so it's safe to call on mouse leave AND when the popup closes via click-away.
  const scheduleCollapse = React.useCallback(() => {
    window.clearTimeout(collapseTimer.current)
    collapseTimer.current = window.setTimeout(() => {
      setSwitcherOpen((open) => {
        if (!open && !isHovering.current && !isFocused.current) setCollapsed(true)
        return open
      })
    }, COLLAPSE_DELAY_MS)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    isHovering.current = false
    scheduleCollapse()
  }, [scheduleCollapse])

  // Close on click-outside (of the whole footer region) and on Escape, and move
  // focus into the popup when it opens.
  React.useEffect(() => {
    if (!switcherOpen) return

    const handlePointer = (event: MouseEvent) => {
      if (footerRef.current && !footerRef.current.contains(event.target as Node)) {
        setSwitcherOpen(false)
        // Popup dismissed by clicking away; run the same delayed collapse so the
        // rail closes if the mouse has since left it.
        scheduleCollapse()
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSwitcher()
        scheduleCollapse()
      }
    }

    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("keydown", handleKey)
    popupRef.current?.focus()

    return () => {
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [switcherOpen, closeSwitcher, scheduleCollapse])

  // The rail overlays content when expanded: the --sidenav-width custom property
  // (which drives the .wb-sidenav-push content margin) stays pinned to the
  // collapsed width so the page never reflows on hover. The rail itself widens
  // over the content via the .collapsed class transition (see sidenav.module.sass).
  React.useEffect(() => {
    document.documentElement.style.setProperty("--sidenav-width", "72px")
  }, [])

  React.useEffect(() => () => window.clearTimeout(collapseTimer.current), [])

  const tenantInitial = (tenant.name?.trim()[0] ?? "W").toUpperCase()
  const logoUrl = tenant.logo?.url
  const showContext = Boolean(context)

  return (
    <aside
      className={classNames(styles.sidenav, { [styles.collapsed]: collapsed })}
      onMouseEnter={expand}
      onMouseLeave={handleMouseLeave}
      // Hover isn't reachable by keyboard or touch: expand while focus is inside
      // the rail and collapse once it leaves, so tabbing through reveals labels.
      onFocusCapture={handleFocus}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          isFocused.current = false
          scheduleCollapse()
        }
      }}
    >
      <div className={styles.header}>
        {logoUrl ? (
          <span className={styles.brandLogoWrap}>
            <img className={styles.brandLogo} src={logoUrl} alt="" />
          </span>
        ) : (
          <div className={styles.brandMark}>{tenantInitial}</div>
        )}
      </div>

      <div className={styles.scrollArea}>
        <nav className={styles.nav}>
          {items.map((item, index) => (
            <a
              key={item.id ?? `nav-${index}`}
              href={item.href}
              className={classNames(styles.navItem, {
                [styles.navItemActive]: item.isActive,
              })}
              aria-current={item.isActive ? "page" : undefined}
              title={collapsed ? item.label : undefined}
            >
              {item.icon && <Icon name={item.icon} />}
              <span className={styles.navLabel}>{item.label}</span>
            </a>
          ))}
        </nav>

        {showContext && context && (
          <div className={styles.context}>
            {context.backHref && (
              <a className={styles.backLink} href={context.backHref}>
                <span aria-hidden="true">&#8592;</span>
                {context.backLabel ?? "Back"}
              </a>
            )}
            {context.title && <div className={styles.contextTitle}>{context.title}</div>}
            {context.items.map((item, index) => (
              <a
                key={item.id ?? `sub-${index}`}
                href={item.href}
                className={classNames(styles.subItem, {
                  [styles.subItemActive]: item.isActive,
                })}
                aria-current={item.isActive ? "page" : undefined}
              >
                {item.label}
              </a>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer} ref={footerRef}>
        <div className={styles.footerActions}>
          {settingsPath && (
            <a
              className={styles.footerLink}
              href={settingsPath}
              title={collapsed ? "Settings" : undefined}
            >
              <Icon name="cog" />
              <span className={styles.footerLinkLabel}>Settings</span>
            </a>
          )}

          {helpPath && (
            <a
              className={styles.footerLink}
              href={helpPath}
              target="_blank"
              rel="noopener noreferrer"
              title={collapsed ? "Help & docs" : undefined}
            >
              <Icon name="question" />
              <span className={styles.footerLinkLabel}>Help &amp; docs</span>
              {!collapsed && <Icon name="external-link" size="small" />}
            </a>
          )}

          <div className={styles.profileTrigger}>
            {switcherOpen && (
              <div
                className={styles.popup}
                ref={popupRef}
                role="dialog"
                aria-label="Switch profiles"
                tabIndex={-1}
              >
                <div className={styles.popupHeader}>
                  <div className={styles.popupTitle}>Switch profiles</div>
                  <button
                    type="button"
                    className={styles.popupClose}
                    aria-label="Close"
                    onClick={closeSwitcher}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4l8 8" />
                      <path d="M12 4l-8 8" />
                    </svg>
                  </button>
                </div>

                <div role="radiogroup">
                  {roles.map((role) => {
                    const active = role.active
                    return (
                      <button
                        type="button"
                        key={`${role.role.id}-${role.role.type}`}
                        className={classNames(styles.profileRow, {
                          [styles.profileRowActive]: active,
                        })}
                        onClick={() => onRoleSelected(role)}
                        role="radio"
                        aria-checked={active}
                      >
                        <span
                          className={classNames(styles.radioOuter, {
                            [styles.radioOuterActive]: active,
                          })}
                        >
                          {active && <span className={styles.radioInner} />}
                        </span>
                        <span className={styles.profileMeta}>
                          <span className={styles.profileMetaName}>{role.role.name}</span>
                          <span className={styles.profileMetaTenant}>{role.tenant}</span>
                        </span>
                        <span className={styles.roleBadge}>{role.role.role_name}</span>
                      </button>
                    )
                  })}
                </div>

                <div className={styles.popupFooter}>
                  <button type="button" className={styles.logoutButton} onClick={onLogout}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 14H3V2h3" />
                      <path d="M10.5 11l3-3-3-3" />
                      <path d="M13.5 8H6" />
                    </svg>
                    Log out
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              ref={profileButtonRef}
              className={styles.profileButton}
              aria-haspopup="dialog"
              aria-expanded={switcherOpen}
              title={collapsed ? currentUser.fullName : undefined}
              onClick={() => setSwitcherOpen((open) => !open)}
            >
              <Icon name="person-circle" />
              <span className={styles.profileInfo}>
                <span className={styles.profileName}>{currentUser.fullName}</span>
                <span className={styles.profileRole}>{currentUser.role}</span>
              </span>
              <Icon
                name="chevron-up"
                size="small"
                className={classNames(styles.chevron, {
                  [styles.chevronOpen]: switcherOpen,
                })}
              />
            </button>
          </div>
        </div>
      </div>
    </aside>
  )
}

export { SideNav }
export type { SideNavProps, SideNavItem, SideNavContext }
